import { Component, computed, effect, inject, input, output } from '@angular/core';
import { PopupService } from '../../services/popup.service';
import { FeedbackService } from '../../services/feedback.service';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { CustomButtonComponent } from '../inputs/custom-button/custom-button.component';

@Component({
  selector: 'lib-popup',
  standalone: true,
  imports: [CommonModule, TranslateModule, CustomButtonComponent],
  templateUrl: './popup.component.html',
})
export class PopupComponent {
  id = input.required<string>();

  // This output will emit the id of the popup when it is closed.
  closed = output<string>();
  confirm = output<void>();
  cancelled = output<void>();

  loading = input(false);
  confirmText = input<string>('');
  cancelText = input<string>('');
  requireConfirmationToClose = input<boolean>(false);
  showConfirm = input<boolean>(true);
  showCancel = input<boolean>(true);

  private _popupService = inject(PopupService);
  private _feedbackService = inject(FeedbackService);
  private _popupState = computed(() => this._popupService.popups().find((p) => p.id === this.id()));

  isVisible = computed(() => !!this._popupState());
  title = computed(() => this._popupState()?.title ?? '');
  zIndex = computed(() => this._popupState()?.zIndex ?? 0);
  size = computed(() => this._popupState()?.size ?? 'lg');

  sizeClasses = computed(() => {
    switch (this.size()) {
      case 'sm':
        return 'sm:max-w-sm';
      case 'md':
        return 'sm:max-w-md';
      case 'xl':
        return 'sm:max-w-xl';
      case '2xl':
        return 'sm:max-w-2xl';
      case '3xl':
        return 'sm:max-w-3xl';
      case '4xl':
        return 'sm:max-w-4xl';
      case '5xl':
        return 'sm:max-w-5xl';
      case '6xl':
        return 'sm:max-w-6xl';
      case '7xl':
        return 'sm:max-w-7xl';
      case 'fullscreen':
        return 'h-screen w-screen max-w-full flex flex-col';
      case 'lg':
      default:
        return 'sm:max-w-lg';
    }
  });

  constructor() {
    // Effect to emit the 'closed' event when the popup is no longer visible
    effect((onCleanup) => {
      const isCurrentlyVisible = this.isVisible();
      onCleanup(() => {
        // This runs when the effect is destroyed or re-runs.
        // If it was visible before and now it's not, it means it was closed.
        if (isCurrentlyVisible && !this.isVisible()) {
          this.closed.emit(this.id());
        }
      });
    });
  }

  onConfirm(): void {
    this.confirm.emit();
  }

  onCancelled(): void {
    const doCancel = () => {
      this.cancelled.emit();
      this._popupService.close(this.id());
    };

    if (this.requireConfirmationToClose()) {
      this._feedbackService.showConfirm({
        title: '¿Deseas cancelar este proceso?',
        message:
          'Se perderá la información que ya has ingresado y deberás realizar el proceso nuevamente.',
        skipTranslation: true,
        cancelLabel: 'Regresar',
        presentation: 'modal',
        onAction: () => doCancel(),
        actionLabel: 'Cancelar y salir',
      });
    } else {
      doCancel();
    }
  }
}
